import React from "react";
import {
  Accordion,
  AttributeHeader,
  AttributeValue,
  CustomMobileAccordionType,
  DataTable,
  DateTimeFormatter,
  Icon,
  IconFormatter,
  TextFormatter,
} from "../../components";
import { DataTableColumn, FileDTO } from "../../types";
import { ColumnType } from "./FileAttachment";
import FileAttachmentSizeFormatter from "./FileAttachmentSizeFormatter";
import LabelFormatter from "./LabelFormatter";

type FileTableProps = {
  /** List of files currently selected */
  files: FileDTO[];
  /** Function for change of selected files */
  deleteFile: (index: number) => void;
  /** Columns to display in table */
  displayColumns?: ColumnType[];
  /** if the input is loading */
  loading?: boolean;
  /** if the component is read only */
  readOnly?: boolean;
};

type FileAttachmentsMobileProps = CustomMobileAccordionType<FileDTO> & {
  data: FileDTO;
};

/**
 * File Table component to display file information
 */
const FileTable: React.FC<React.PropsWithChildren<FileTableProps>> = ({
  files = [],
  deleteFile,
  displayColumns = ["fileName", "lastModified", "size", "fileType"],
  loading = false,
  readOnly = false,
}) => {
  const FileAttachmentsMobile = (props: FileAttachmentsMobileProps) => {
    const { data, index, ...rest } = props;
    return (
      <Accordion {...rest} accordionKey={data.name}>
        <Accordion.Header>
          <div className="d-flex align-items-center justify-content-between">
            <TextFormatter value={data.name} />
            <div>
              <Icon iconName="fa-trash" onClick={() => onFileDelete(index)} />
            </div>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <div>
            <AttributeHeader>File Name</AttributeHeader>
            <AttributeValue>
              <TextFormatter value={data.name} />
            </AttributeValue>
          </div>
          <div>
            <AttributeHeader>File Type</AttributeHeader>
            <AttributeValue>
              <LabelFormatter value={data.type} id="fileAttachment" />
            </AttributeValue>
          </div>
          <div>
            <AttributeHeader>Size</AttributeHeader>
            <AttributeValue>
              <FileAttachmentSizeFormatter value={data.size} />
            </AttributeValue>
          </div>
          <div>
            <AttributeHeader>Last Modified</AttributeHeader>
            <AttributeValue>
              <DateTimeFormatter value={data.lastModified} />
            </AttributeValue>
          </div>
        </Accordion.Body>
      </Accordion>
    );
  };

  const height = 35 + (files ? files.length : 1) * 35;

  const onFileDelete = (index: number) => {
    deleteFile(index);
  };

  const Columns: DataTableColumn<FileDTO>[] = [
    {
      key: "name",
      name: "File Name",
      width: 300,
      visible: displayColumns?.includes("fileName"),
    },
    {
      key: "lastModified",
      name: "Last Modified",
      width: 200,
      readonlyFormatter: DateTimeFormatter,
      visible: displayColumns?.includes("lastModified"),
    },
    {
      key: "size",
      name: "Size",
      width: 100,
      readonlyFormatter: FileAttachmentSizeFormatter,
      visible: displayColumns?.includes("size"),
    },
    {
      key: "type",
      name: "File Type",
      width: 300,
      visible: displayColumns?.includes("fileType"),
    },
  ];

  if (!readOnly) {
    Columns.unshift({
      key: "icon",
      name: "",
      readonlyFormatter: IconFormatter,
      icon: "fa-trash",
      onClick: onFileDelete,
      width: 50,
      cellClass: "text-center",
    });
  }

  return (
    <DataTable
      data={files}
      columns={Columns}
      accordion={FileAttachmentsMobile}
      tableHeights={{
        maxTableHeight: height,
        maxHeightOffset: 0,
        autogrow: false,
      }}
      loading={loading}
    />
  );
};

export default FileTable;
